import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { ApiError } from '../api/client';
import { listRondoUsers, listRondoSegments, type RondoUserListItem, type RondoSegmentItem } from '../api/rondo';
import { ServiceBar } from '../shell/ServiceBar';
import { Button } from '../components/arc/button/button';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { useRondoNav } from './RondoNav';
import './users-seznam.css';

const COUNTRY_FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };

function initials(name: string) {
  const w = name.split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] ?? '') + (w[1]?.[0] ?? '')).toUpperCase();
}

const money = (n: number) =>
  n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const intFmt = (n: number) => n.toLocaleString('cs-CZ');
const loginDate = (iso: string | null) => iso
  ? new Date(iso).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' })
  : '—';

export function UsersSeznamPage() {
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState('all');
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [activity, setActivity] = useState('all');
  const [segment, setSegment] = useState('all');

  const [segments, setSegments] = useState<RondoSegmentItem[]>([]);
  const [rows, setRows] = useState<RondoUserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listRondoSegments()
      .then((res) => { if (!cancelled) setSegments(res.items); })
      .catch(() => { if (!cancelled) setSegments([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      listRondoUsers({
        search: query.trim() || undefined,
        country: country !== 'all' ? country : undefined,
        segment: segment !== 'all' ? segment : undefined,
        activeDays: activity !== 'all' && activity !== 'inactive90' ? Number(activity) : undefined,
        inactive90: activity === 'inactive90',
        limit,
        offset,
      })
        .then((res) => {
          if (cancelled) return;
          setRows(res.items);
          setTotal(res.total);
          setError(null);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof ApiError ? e.message : 'Načtení selhalo');
          setRows([]);
          setTotal(0);
        })
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, country, segment, limit, activity, offset]);

  useEffect(() => { setOffset(0); }, [query, country, segment, limit, activity]);

  const nav = useRondoNav();

  function clearFilters() {
    setQuery('');
    setCountry('all');
    setLimit(10);
    setActivity('all');
    setSegment('all');
    setOffset(0);
  }

  const visibleActive = rows.filter((r) => r.isActive90d).length;
  const visibleMarkets = new Set(rows.map((r) => r.country).filter(Boolean)).size;
  const tableRows = rows.map(row => ({ ...row, detail: row.id }));
  type TableRow = (typeof tableRows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'name', label: 'Uživatel', width: 190, render: (_, row) => <button type="button" className="rondo-arc-user-link" onClick={() => nav.openUserDetail(row.id)}><span className="rondo-arc-user-avatar">{initials(row.name)}</span><span>{row.name}</span></button> },
    { key: 'country', label: 'Země', width: 75, render: (_, row) => <span className="rondo-arc-user-country">{COUNTRY_FLAG[row.country] ?? ''} {row.country}</span> },
    { key: 'segments', label: 'Segmenty', width: 125, sortable: false, render: (_, row) => row.segments.length ? <span className="rondo-arc-user-segments">{row.segments.map(segment => <span key={segment.name} style={{ backgroundColor: segment.color || '#64748b' }}>{segment.name}</span>)}</span> : '—' },
    { key: 'cashBalance', label: 'Zůstatek', numeric: true, width: 118, render: (_, row) => `${money(row.cashBalance)} ${row.currency}` },
    { key: 'credits', label: 'Rondony', numeric: true, width: 100, render: (_, row) => intFmt(row.credits) },
    { key: 'tip30d', label: 'Tipov. 30d', numeric: true, width: 102, render: (_, row) => row.tip30d ? intFmt(row.tip30d) : '—' },
    { key: 'leads', label: 'Leady', numeric: true, width: 70, render: (_, row) => row.leads || '—' },
    { key: 'shScore', label: 'SH', numeric: true, width: 58, render: (_, row) => row.shScore ?? '—' },
    { key: 'lastActiveAt', label: 'Posl. login', width: 125, render: (_, row) => loginDate(row.lastActiveAt) },
    { key: 'isActive90d', label: 'Aktivita 90d', width: 108, render: (_, row) => <span className={`rondo-arc-user-status ${row.isActive90d ? 'is-active' : ''}`}>{row.status === 'blocked' ? 'Blokován' : row.isActive90d ? 'Aktivní' : 'Mimo období'}</span> },
    { key: 'detail', label: '', width: 48, sortable: false, render: (_, row) => <button type="button" className="rondo-arc-user-detail" aria-label={`Detail uživatele ${row.name}`} onClick={() => nav.openUserDetail(row.id)}><ChevronRight size={16} aria-hidden="true" /></button> },
  ];

  return (
    <section className="rondo-arc-users-list p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">UŽIVATELÉ</div><h1>Seznam</h1><p>Kompletní databáze uživatelů Ronda</p></div><span className="prototype-count">
        {loading
          ? 'Načítám…'
          : `${intFmt(rows.length)} uživatelů${total > rows.length ? ` z celkem ~${intFmt(total)}` : ''}`}
      </span></header>

      <div className="rondo-arc-users-list-metrics">
        <MetricCard label="Uživatelů celkem" value={total} context="Celá databáze" />
        <MetricCard label="Aktivní na stránce" value={visibleActive} context="Přihlášení do 90 dní" />
        <MetricCard label="Zobrazené trhy" value={visibleMarkets} context="Podle aktuálního filtru" />
        <MetricCard label="Záznamy na stránce" value={rows.length} context={`Stránka ${Math.floor(offset / Math.max(1, limit)) + 1}`} />
      </div>

      <ServiceBar
        selects={[
          { label: 'Země', value: country, onChange: setCountry, options: [
            { value: 'all', label: 'Všechny' }, { value: 'CZ', label: '🇨🇿 CZ - Česko' },
            { value: 'SK', label: '🇸🇰 SK - Slovensko' }, { value: 'RO', label: '🇷🇴 RO - Rumunsko' } ] },
          { label: 'Aktivita', value: activity, onChange: setActivity, options: [
            { value: '7', label: 'Aktivní 7 dní' }, { value: '30', label: 'Aktivní 30 dní' },
            { value: '90', label: 'Aktivní 90 dní' }, { value: 'inactive90', label: 'Mimo 90 dní' },
            { value: 'all', label: 'Všichni' } ] },
          { label: 'Segment', value: segment, onChange: setSegment, minWidth: 180, options: [
            { value: 'all', label: 'Všechny' },
            ...segments.map((s) => ({ value: String(s.id), label: `${s.name} (${s.country})` })) ] },
          { label: 'Limit', value: String(limit), onChange: (v) => setLimit(Number(v)), options: [
            { value: '10', label: '10' }, { value: '25', label: '25' }, { value: '50', label: '50' },
            { value: '100', label: '100' }, { value: '200', label: '200' } ] },
        ]}
        onReset={clearFilters}
        search={query}
        onSearch={setQuery}
        searchPlaceholder="jméno / email / telefon / ID…"
        variant="seg"
      />

      <div className="rondo-arc-users-list-table">
        {error && <p role="alert" className="rondo-arc-users-error">{error}</p>}
        {loading ? <p className="rondo-arc-users-loading">Načítám uživatele…</p> : <SortableDataTable
          rows={tableRows}
          columns={columns}
          rowKey="id"
          caption="Seznam uživatelů Ronda"
          emptyMessage="Žádný uživatel neodpovídá filtru."
        />}
      </div>

      <div className="rondo-arc-users-footer">
        <div>
          Stránka {Math.floor(offset / Math.max(1, limit)) + 1}
          {total > 0 ? ` · ~${intFmt(total)} uživatelů` : ''}
        </div>
        <div className="rondo-arc-users-page-buttons">
          <Button variant="secondary" size="sm" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - limit))}>Předchozí</Button>
          <Button variant="secondary" size="sm" disabled={loading || rows.length < limit || offset + rows.length >= total} onClick={() => setOffset(offset + limit)}>Další</Button>
        </div>
      </div>
    </section>
  );
}
